
import React , { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  Tooltip
} from "@mui/material";


// DD/MM/YYYY only
const defaultFormatDate = (iso) => {
  if (!iso) return "-";
  const d = moment(iso);
  return d.isValid() ? d.format("DD/MM/YYYY") : "-";
};

const StatusChip = ({ value }) => {
  const v = String(value ?? "").toUpperCase();
  if (!v) return <Typography variant="body2">-</Typography>;
  let color = "default";
  if (v === "POST") color = "success";
  if (v === "ENTER") color = "warning";
  return <Chip size="small" color={color} label={v} sx={{ width: "fit-content" }} />;
};

const InfoPair = ({ label, value, isStatus = false, isPromoLoc = false }) => {
  const theme = useTheme();

  // measure overflow only for promo locations
  const ref = useRef(null);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    if (!isPromoLoc || !ref.current) return;
    const el = ref.current;
    // allow layout to settle, then measure
    const id = requestAnimationFrame(() => {
      // If content needs more space than given height, it's clamped
      setClamped(el.scrollHeight > el.clientHeight + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [isPromoLoc, value]);

  const content = isStatus ? (
    <Box sx={{ display: "flex", justifyContent: "flex-start", columnGap: 1 }}>
      <StatusChip value={value} />
    </Box>
  ) : (
    <Typography
      ref={isPromoLoc ? ref : null}
      variant="body2"
      sx={{
        color: "#334155",
        fontWeight: 600,

        // default behavior for non-promo fields
        overflow: isPromoLoc ? "hidden" : "hidden",
        textOverflow: isPromoLoc ? "ellipsis" : "ellipsis",
        whiteSpace: isPromoLoc ? "normal" : "nowrap",

        // multi-line clamp (3 lines) for promo locations
        ...(isPromoLoc && {
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          wordBreak: "break-word",
        }),
      }}
      title={isPromoLoc ? undefined : value} // keep original tooltip behavior for non-promo fields
    >
      {value || "-"}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "31% 69%",
          sm: "17% 83%",
          md: isPromoLoc ? "17% 83%" : "42% 58%",
          lg: isPromoLoc ? "13% 87%" : "45% 55%",
          xl: isPromoLoc ? "13% 87%" : "45% 55%",
        },
        columnGap: 1,
        width: "100%",
        alignItems: isPromoLoc ? "start" : "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>

      {isPromoLoc ? (
        // Show tooltip with full value only when clamped
        clamped ? (
          <Tooltip title={value || "-"} arrow placement="top-start">
            <Box>{content}</Box>
          </Tooltip>
        ) : (
          content
        )
      ) : (
        content
      )}
    </Box>
  );
};

const Pill = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Chip
      variant="outlined"
      size="small"
      label={
        <Typography variant="caption" component="span" sx={{ fontWeight: 600 }}>
          <span style={{ color: theme.palette.primary.main }}>{label}</span>{" "}
          <span style={{ color: "#334155" }}>{value}</span>
        </Typography>
      }
      // sx={{ bgcolor: "background.paper" }}
    />
  );
};

// 🔑 Map pageType → status field key + label
const PAGE_STATUS_MAP = {
  ProofReading: { key: "ProofRead_Status", label: "Status" },
  Print: { key: "Print_Status", label: "Print Status" },
};

const SideNavHeader = ({ selectedRow = {},pageType, sx, formatDate = defaultFormatDate }) => {
  const { TransId, TransDt, TransLocName, FromDt, ToDt, PromoTitle, PromoLocName } =
    selectedRow;

   // pick the single status field based on pageType
  const statusSpec = PAGE_STATUS_MAP[pageType];
  const selectedStatusValue =
    statusSpec && Object.prototype.hasOwnProperty.call(selectedRow, statusSpec.key)
      ? selectedRow[statusSpec.key]
      : undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "grey.50",
        ...sx,
      }}
    >
<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
  <Pill label="Trans ID :" value={TransId || "-"} />
  <Pill label="Date :" value={formatDate(TransDt)} />
  <Pill label="Loc :" value={TransLocName || "-"} />
  <Pill label="Promo Title :" value={PromoTitle || "-"} />
</Box>

      <Divider sx={{ my: 1 }} />

      {/* Single-card content */}
      <Grid container spacing={{ xs: 0.5, md: 1 }}>
        <Grid item xs={12} md={2.5} lg={2} >
          <Box sx={{ display: "grid", rowGap: 1.25 }}>
            <InfoPair label="From Date" value={formatDate(FromDt)} />
            <InfoPair label="To Date" value={formatDate(ToDt)} />
            {statusSpec && (
              <InfoPair
                label={statusSpec?.label}
                value={selectedStatusValue}
                isStatus={true}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={9.5} lg={10} >
          <Box sx={{ display: "grid", rowGap: 1.25 }}>
            <InfoPair isPromoLoc={true} label="Promo Locations" value={PromoLocName || "-"} />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SideNavHeader;

